import { FC, useRef, useState, useEffect, useCallback } from "react";
import { TextField } from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { HTMLInputTypeAttribute } from "react";
import { InputTextFieldProps } from "./types/FieldTypes";

interface InputGoogleAutocompleteProps extends InputTextFieldProps {
    type?: HTMLInputTypeAttribute;
}

const InputGoogleAutocomplete: FC<InputGoogleAutocompleteProps> = ({
    placeholder,
    FieldInputProps: {
        state: { value },
        actions: { update, blur, focus, touch },
    },
    fullWidth,
    onChange,
    sx,
    inputProps: {
        onFocus: inputPropsFocus,
        onBlur: inputPropsBlur,
        ...inputProps
    } = {},
    error,
    ...rest
}) => {
    const placesLib = useMapsLibrary("places");
    const inputRef = useRef<HTMLInputElement | null>(null);
    const autocompleteRef =
        useRef<google.maps.places.Autocomplete | null>(null);

    /**
     * Local value to prevent cursor jumping
     */
    const [localValue, setLocalValue] = useState<string>(() => value ?? "");

    useEffect(() => {
        setLocalValue(value ?? "");
    }, [value]);

    /**
     * Initialise Google Places Autocomplete
     */
    useEffect(() => {
        if (!placesLib || !inputRef.current || autocompleteRef.current) return;

        autocompleteRef.current = new placesLib.Autocomplete(
            inputRef.current,
            {
                fields: ["formatted_address"],
                types: ["geocode"],
                componentRestrictions: { country: ["au", "cx", "cc", "nf", "fk"] }
            }
        );

        autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current?.getPlace();
            if (!place?.formatted_address) return;

            setLocalValue(place.formatted_address);
            update(place.formatted_address, true, true, true);
        });
    }, [placesLib, update]);

    /**
     * Change handler
     */
    const onChangeCombined = useCallback(
        (
            event: React.ChangeEvent<
                HTMLTextAreaElement | HTMLInputElement
            >
        ) => {
            const newValue = event.target.value;

            setLocalValue(newValue);

            if (onChange) {
                onChange(event);
            }

            update(
                newValue && newValue !== "" ? newValue : undefined as any,
                true,
                true,
                true
            );
        },
        [onChange, update]
    );

    /**
     * Focus handler
     */
    const onFocusCombined = useCallback(
        (
            event: React.FocusEvent<
                HTMLTextAreaElement | HTMLInputElement
            >
        ) => {
            if (inputPropsFocus) {
                inputPropsFocus(event);
            }

            focus();
        },
        [inputPropsFocus, focus]
    );

    /**
     * Blur handler
     */
    const onBlurCombined = useCallback(
        (
            event: React.FocusEvent<
                HTMLTextAreaElement | HTMLInputElement
            >
        ) => {
            if (inputPropsBlur) {
                inputPropsBlur(event);
            }

            blur();
            touch();
        },
        [inputPropsBlur, blur, touch]
    );

    return (
        <TextField
            fullWidth={fullWidth ?? true}
            size="small"
            inputRef={inputRef}
            inputProps={{
                ...inputProps,
                onBlur: onBlurCombined,
                onFocus: onFocusCombined,
            }}
            value={localValue}
            placeholder={placeholder}
            onChange={onChangeCombined}
            error={error}
            sx={{ ...sx }}
            {...(rest as any)}
        />
    );
};

export default InputGoogleAutocomplete;
